/**
 * useSubscription.ts — POST /api/broker/v1/subscriptions hook
 *
 * On trigger: POSTs { service_id } with Bearer JWT.
 * Expected response: { status: 'pending' | 'created', message?: string }
 *
 * Design-contract §11: Binding Lifecycle (order variant)
 * Ref: DOPLA-602 — marketplace user subscription flow
 */

import { useState, useCallback } from 'react';

export type SubscriptionState = 'idle' | 'submitting' | 'success' | 'error';

export interface UseSubscriptionResult {
  state: SubscriptionState;
  trigger: () => Promise<void>;
  reset: () => void;
}

export function useSubscription(serviceId: string, token: string | null): UseSubscriptionResult {
  const [state, setState] = useState<SubscriptionState>('idle');

  const trigger = useCallback(async () => {
    if (!token) {
      setState('error');
      return;
    }
    setState('submitting');
    try {
      const res = await fetch('/api/broker/v1/subscriptions', {
        cache: 'no-store',
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ service_id: serviceId }),
      });
      if (!res.ok) {
        setState('error');
        return;
      }
      setState('success');
    } catch {
      setState('error');
    }
  }, [serviceId, token]);

  const reset = useCallback(() => setState('idle'), []);

  return { state, trigger, reset };
}
